import { Controller } from "@hotwired/stimulus"

// Register all player web components
import "vidstack/player"
import "vidstack/player/ui"
import "vidstack/icons"
import "media-icons/element"

const YOUTUBE_ORIGINS = new Set(["https://www.youtube.com", "https://www.youtube-nocookie.com"])

// Connects to data-controller="video-player"
export default class extends Controller {
  static targets = ["chaptersTrack", "captionsTrack", "openLink", "bookmarkWrapper"]
  static values = {
    source: String,
    timestamp: { type: Number, default: 0 },
    clipStart: Number,
    clipEnd: Number,
    poster: String,
    controls: { type: Object, default: { show_controls: true, mode: "full" } },
    openUrl: String,
    chaptersUrl: String,
    captionsUrl: String
  }

  // =============================================================================
  // LIFECYCLE METHODS
  // =============================================================================

  async connect() {
    this.player = this.element // The media-player element itself
    this.isPlaying = false // Track current playing state
    this.pendingPlay = false // Track if we're about to start playing

    this.applyStoredPlaybackState()
    await this.initializePlayer()
    this.setupGlobalEventListeners()
    this.setupEventListeners()
    this.setupExternalControls()
  }

  disconnect() {
    this.removeGlobalEventListeners()
    this.removePlayerEventListeners()
    this.removeTimestampEventListeners()
    this.removeExternalControlListeners()
  }

  // =============================================================================
  // EVENT LISTENER SETUP
  // =============================================================================

  setupGlobalEventListeners() {
    // Listen for other videos starting to play
    this.boundHandleOtherVideoPlay = this.handleOtherVideoPlay.bind(this)
    document.addEventListener("video-player:play", this.boundHandleOtherVideoPlay)

    this.boundHandleYouTubeMessage = this.handleYouTubeMessage.bind(this)
    window.addEventListener("message", this.boundHandleYouTubeMessage)
  }

  removeGlobalEventListeners() {
    if (this.boundHandleOtherVideoPlay) {
      document.removeEventListener("video-player:play", this.boundHandleOtherVideoPlay)
    }
    if (this.boundHandleYouTubeMessage) {
      window.removeEventListener("message", this.boundHandleYouTubeMessage)
    }
  }

  handleYouTubeMessage(event) {
    if (!YOUTUBE_ORIGINS.has(event.origin)) return

    const iframe = this.player?.querySelector("iframe.vds-youtube")
    if (!iframe || event.source !== iframe.contentWindow) return

    let message
    try {
      message = typeof event.data === "string" ? JSON.parse(event.data) : event.data
    } catch {
      return
    }
    if (message?.event !== "infoDelivery") return

    // YouTube's long-lived but undocumented rendered video area. See #638.
    const rect = message.info?.videoContentRect
    if (!Number.isFinite(rect?.width) || !Number.isFinite(rect?.height)) return
    if (rect.width <= 0 || rect.height <= 0) return

    const orientation = rect.height > rect.width ? "portrait" : "landscape"
    if (this.player.dataset.sourceOrientation === orientation) return

    this.player.dataset.sourceOrientation = orientation
  }

  removePlayerEventListeners() {
    if (!this.player) return

    // Remove all player event listeners using stored bound functions
    if (this.boundTimeUpdateHandler) {
      this.player.removeEventListener("time-update", this.boundTimeUpdateHandler)
    }
    if (this.boundPlayingHandler) {
      this.player.removeEventListener("playing", this.boundPlayingHandler)
    }
    if (this.boundPauseHandler) {
      this.player.removeEventListener("pause", this.boundPauseHandler)
    }
    if (this.boundSeekedHandler) {
      this.player.removeEventListener("seeked", this.boundSeekedHandler)
    }
    if (this.boundEndedHandler) {
      this.player.removeEventListener("ended", this.boundEndedHandler)
    }
  }

  removeTimestampEventListeners() {
    if (this.boundInitialTimestamp) {
      this.player.removeEventListener("can-play", this.boundInitialTimestamp)
      this.player.removeEventListener("loaded-metadata", this.boundInitialTimestamp)
    }

    // Remove timestamp button listeners
    if (this.timestampButtons) {
      this.timestampButtons.forEach((button) => {
        if (button.boundClickHandler) {
          button.removeEventListener("click", button.boundClickHandler)
        }
      })
    }
  }

  removeExternalControlListeners() {
    if (this.boundExternalSeekPlay) {
      this.player.removeEventListener("video-player:external:seekandplay", this.boundExternalSeekPlay)
    }
    if (this.boundExternalPause) {
      this.player.removeEventListener("video-player:external:pause", this.boundExternalPause)
    }
  }

  // =============================================================================
  // VISIBILITY & FOCUS HANDLERS (Removed)
  // =============================================================================

  handleOtherVideoPlay(event) {
    // If another player instance (not this one) starts, pause this one
    if (event.target !== this.element && this.player && this.isPlaying) {
      this.pauseVideo().catch(() => {
        // Ignore errors when pausing other videos
      })
    }
  }

  // =============================================================================
  // PLAYER INITIALIZATION
  // =============================================================================

  async initializePlayer() {
    // The web component handles all the UI - we just need to set up event listeners
    this.setupPlayerEvents()

    // Live players have no timestamp; on-demand players may start at zero.
    if (this.hasTimestampValue) {
      this.setupInitialTimestamp()
    }
  }

  applyStoredPlaybackState() {
    if (!this.player) return

    if (this.hasSourceValue && this.sourceValue) this.player.setAttribute("src", this.sourceValue)
    if (this.hasClipStartValue) this.applyClipStart(this.clipStartValue)
    if (this.hasClipEndValue) this.applyClipEnd(this.clipEndValue)
    if (this.hasTimestampValue) this.applyTimestamp(this.timestampValue)
    if (this.hasPosterValue && this.posterValue) this.updatePoster(this.posterValue)
  }

  setupInitialTimestamp() {
    this.boundInitialTimestamp = () => {
      this.applyTimestamp(this.timestampValue)
    }

    // HLS can announce can-play before native metadata exists. Safari can then
    // discard that early seek, so reapply it on Vidstack's loaded-metadata event.
    // Keep both listeners for subsequent source changes (e.g. playlists).
    this.player.addEventListener("can-play", this.boundInitialTimestamp)
    this.player.addEventListener("loaded-metadata", this.boundInitialTimestamp)

    if (this.player.state?.canPlay) {
      this.boundInitialTimestamp()
    }
  }

  setupPlayerEvents() {
    if (!this.player) return

    // Wait for player to be ready
    this.player.addEventListener("can-play", () => {
      this.setupPlayerEventListeners()
    })

    // Also check if player is already ready
    if (this.player.state?.canPlay) {
      this.setupPlayerEventListeners()
    }
  }

  setupPlayerEventListeners() {
    if (!this.player) return

    // Store bound event handlers for cleanup
    this.boundTimeUpdateHandler = (event) => {
      const duration = this.player.state?.duration
      this.dispatch("timeupdate", {
        detail: {
          currentTime: event.detail.currentTime,
          duration: duration,
          percent: duration ? (event.detail.currentTime / duration) * 100 : 0
        }
      })
    }

    this.boundPlayingHandler = () => {
      this.isPlaying = true
      this.pendingPlay = false
      this.dispatch("play", { detail: { videoSource: this.sourceValue } })
    }

    this.boundPauseHandler = () => {
      this.isPlaying = false
      this.pendingPlay = false
      this.dispatch("pause", { detail: { videoSource: this.sourceValue } })
    }

    this.boundSeekedHandler = (event) => {
      this.dispatch("seeked", {
        detail: {
          currentTime: event.detail.currentTime,
          videoSource: this.sourceValue
        }
      })
    }

    this.boundEndedHandler = () => {
      this.isPlaying = false
      this.pendingPlay = false
      this.dispatch("ended", {
        detail: {
          videoSource: this.sourceValue
        }
      })
    }

    // Add event listeners
    this.player.addEventListener("time-update", this.boundTimeUpdateHandler)
    this.player.addEventListener("playing", this.boundPlayingHandler)
    this.player.addEventListener("pause", this.boundPauseHandler)
    this.player.addEventListener("seeked", this.boundSeekedHandler)
    this.player.addEventListener("ended", this.boundEndedHandler)
  }

  // =============================================================================
  // TIMESTAMP HANDLING
  // =============================================================================

  setupEventListeners() {
    // Add click event listeners to all timestamp buttons
    this.timestampButtons = document.querySelectorAll(".timestamp-btn")
    this.timestampButtons.forEach((button) => {
      const clickHandler = () => {
        const timestamp = button.getAttribute("data-timestamp")
        this.jumpToTimestamp(timestamp)
      }
      // Store the bound handler for cleanup
      button.boundClickHandler = clickHandler
      button.addEventListener("click", clickHandler)
    })
  }

  // Allow other controllers/components to control this player instance
  setupExternalControls() {
    this.boundExternalSeekPlay = (event) => {
      const { seconds, source } = event.detail || {}
      this.seekAndPlay(seconds || 0, source || null).catch(() => {})
    }
    this.boundExternalPause = () => {
      this.pauseVideo().catch(() => {})
    }
    this.player.addEventListener("video-player:external:seekandplay", this.boundExternalSeekPlay)
    this.player.addEventListener("video-player:external:pause", this.boundExternalPause)
  }

  jumpToTimestamp(timestamp) {
    const seconds = this.timestampToSeconds(timestamp)
    this.syncCurrentTimeAttribute(seconds)
    this.dispatch("timestampJumped", { detail: { seconds, timestamp } })
  }

  timestampToSeconds(timestamp) {
    const parts = timestamp.split(":").map(Number)
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2]
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1]
    }
    return 0
  }

  // =============================================================================
  // PUBLIC API METHODS
  // =============================================================================

  // Public method that can be called from other controllers
  seekTo(seconds) {
    this.syncCurrentTimeAttribute(seconds)
  }

  // Public method to get current time
  getCurrentTime() {
    if (this.player?.state) {
      return Promise.resolve(this.player.state.currentTime)
    }
    return Promise.reject(new Error("Player not initialized"))
  }

  // Public method to check if video is currently playing
  isVideoPlaying() {
    return this.isPlaying
  }

  // =============================================================================
  // PLAYBACK CONTROLS
  // =============================================================================

  // Start video playback
  startVideo() {
    if (!this.player) {
      return Promise.reject(new Error("Player not initialized"))
    }

    this.pendingPlay = true

    try {
      const playPromise = this.player.play()
      this.dispatch("videoStarted", { detail: { videoSource: this.sourceValue } })
      return playPromise || Promise.resolve()
    } catch (error) {
      this.pendingPlay = false
      return Promise.reject(error)
    }
  }

  // Set clip times for section playback
  setClipTimes(clipStart, clipEnd) {
    if (!this.player) {
      return Promise.reject(new Error("Player not initialized"))
    }

    try {
      if (clipStart !== undefined && clipStart !== null) {
        this.clipStartValue = Number(clipStart)
        this.applyClipStart(clipStart)
      }
      if (clipEnd !== undefined && clipEnd !== null) {
        this.clipEndValue = Number(clipEnd)
        this.applyClipEnd(clipEnd)
      }
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  // Convenience method to seek to a timestamp and optionally start playing
  seekAndPlay(seconds, videoSource = null, clipStart = null, clipEnd = null, chaptersUrl = null, captionsUrl = null) {
    if (!this.player) {
      return Promise.reject(new Error("Player not initialized"))
    }

    // Set clip times before converting the absolute timestamp to a clip offset.
    if (clipStart !== null || clipEnd !== null) {
      this.setClipTimes(clipStart, clipEnd).catch(() => {
        // Ignore errors setting clip times, continue with playback
      })
    }

    this.syncCurrentTimeAttribute(seconds)

    // If a different video source is provided, load it first
    if (videoSource && videoSource !== this.sourceValue) {
      // Update chapters if provided (only when switching videos)
      if (chaptersUrl) {
        this.updateChaptersTrack(chaptersUrl)
      }
      this.updateCaptionsTrack(captionsUrl)

      return this.loadNewVideo(videoSource, seconds).then(() => {
        return this.startVideo()
      }) // Always start playing when switching videos
    }

    // Play the current video at the timestamp applied above.
    try {
      // Always (re)start — calling play() on an already-playing video is a
      // no-op, and skipping it based on the isPlaying flag caused stale state
      // bugs when rapidly switching between reel cards.
      return this.startVideo()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  // Pause video playback
  pauseVideo() {
    if (!this.player) {
      return Promise.reject(new Error("Player not initialized"))
    }

    try {
      const pausePromise = this.player.pause()
      this.dispatch("videoPaused", { detail: { videoSource: this.sourceValue } })
      return pausePromise || Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  // Toggle play/pause
  togglePlayPause() {
    if (this.player.paused) {
      this.player.play()
    } else {
      this.player.pause()
    }
  }

  // Action: Handle click on video to toggle play/pause
  toggleVideo(event) {
    event.preventDefault()
    this.togglePlayPause().catch(() => {
      // Silently handle errors
    })
  }

  updatePlaylistControls({ openUrl, bookmarkControl, poster } = {}) {
    if (openUrl) {
      this.openUrlValue = openUrl
      if (this.hasOpenLinkTarget) this.openLinkTarget.href = openUrl
    }

    if (poster) this.updatePoster(poster)
    if (bookmarkControl) this.updateBookmarkControl(bookmarkControl)
  }

  updatePoster(poster) {
    this.posterValue = poster
    this.player.setAttribute("poster", poster)
    this.player.querySelector("media-poster")?.setAttribute("src", poster)
  }

  updateBookmarkControl(sourceControl) {
    if (!this.hasBookmarkWrapperTarget) return

    const targetControl = this.bookmarkWrapperTarget.querySelector('[data-bookmark-manager-target~="control"]')
    if (!targetControl) return
    ;["bookmarkKey", "bookmarkableType", "bookmarkableId", "bookmarkReturnTo", "bookmarkAuthPath", "bookmarkCompletionPath"].forEach(
      (key) => {
        targetControl.dataset[key] = sourceControl.dataset[key]
      }
    )

    targetControl.title = sourceControl.title
    targetControl.setAttribute("aria-label", sourceControl.getAttribute("aria-label") || sourceControl.title)

    const bookmarkManager = this.application.getControllerForElementAndIdentifier(document.body, "bookmark-manager")
    if (bookmarkManager?.ready) {
      bookmarkManager.renderControl(targetControl)
    } else {
      this.renderBookmarkControl(targetControl, sourceControl.getAttribute("aria-pressed") === "true")
    }
  }

  renderBookmarkControl(control, saved) {
    control.setAttribute("aria-pressed", saved.toString())
    control.classList.toggle("text-primary", saved)
    control.querySelector('[data-bookmark-icon="saved"]')?.classList.toggle("hidden", !saved)
    control.querySelector('[data-bookmark-icon="unsaved"]')?.classList.toggle("hidden", saved)
  }

  // Open the full video detail page at the current absolute timestamp.
  // Updates the link href with the live playback position, then lets the
  // default click (Turbo Drive) handle navigation — preserving autoplay.
  openAtCurrentTime(event) {
    if (!this.player || !this.hasOpenUrlValue || !this.openUrlValue) return
    const state = this.player.state || {}
    const real = state.realCurrentTime
    const clipStart = state.clipStartTime || 0
    const current = state.currentTime || 0
    let seconds = real != null ? real : current + clipStart
    seconds = Math.max(0, Math.floor(seconds))

    this.syncCurrentTimeAttribute(seconds)

    const url = new URL(this.openUrlValue, window.location.origin)
    url.searchParams.set("t", String(seconds))
    event.currentTarget.href = url.toString()
    // Let the default click proceed with the updated href
  }

  timestampValueChanged(value) {
    if (!this.player) return

    this.applyTimestamp(value)
  }

  clipStartValueChanged(value) {
    if (!this.player) return

    this.applyClipStart(value)
  }

  clipEndValueChanged(value) {
    if (!this.player) return

    this.applyClipEnd(value)
  }

  syncCurrentTimeAttribute(seconds = null) {
    if (!this.player) return

    const value = seconds ?? this.player.state?.realCurrentTime ?? this.player.state?.currentTime ?? this.player.currentTime
    if (value == null || Number.isNaN(Number(value))) return

    const normalized = Math.max(0, Math.floor(Number(value)))
    this.timestampValue = normalized
    this.applyTimestamp(normalized)
  }

  applyTimestamp(value) {
    if (!this.player || value == null || Number.isNaN(Number(value))) return

    const normalized = Math.max(0, Math.floor(Number(value)))
    // App timestamps are absolute; Vidstack's current-time and seek API are
    // clip-relative. Convert only at this boundary, including DOM restoration.
    const offset = Math.max(0, normalized - this.player.clipStartTime)
    this.player.setAttribute("current-time", offset)

    // The request API queues until ready and still seeks when the displayed
    // relative time is zero but Safari's native video is before the clip start.
    this.player.remoteControl.seek(offset)
  }

  applyClipStart(value) {
    if (!this.player || value == null || Number.isNaN(Number(value))) return

    const normalized = Math.max(0, Number(value))
    this.player.clipStartTime = normalized
    this.player.setAttribute("clip-start-time", normalized)
    // Update seek bounds before a same-source clip switch seeks synchronously.
    this.player.remoteControl.changeClipStart(normalized)
  }

  applyClipEnd(value) {
    if (!this.player || value == null || Number.isNaN(Number(value))) return

    const normalized = Math.max(0, Number(value))
    this.player.clipEndTime = normalized
    this.player.setAttribute("clip-end-time", normalized)
    this.player.remoteControl.changeClipEnd(normalized)
  }

  // =============================================================================
  // TRACKS MANAGEMENT
  // =============================================================================

  updateChaptersTrack(chaptersUrl) {
    if (!chaptersUrl || !this.hasChaptersTrackTarget) return

    this.chaptersUrlValue = chaptersUrl

    // Clone the existing track element
    const oldTrack = this.chaptersTrackTarget
    const newTrack = oldTrack.cloneNode(true)

    // Update the src on the new track
    newTrack.src = chaptersUrl

    // Replace the old track with the new one
    oldTrack.parentNode.replaceChild(newTrack, oldTrack)
  }

  updateCaptionsTrack(captionsUrl) {
    if (!captionsUrl || !this.hasCaptionsTrackTarget) return

    this.captionsUrlValue = captionsUrl

    const oldTrack = this.captionsTrackTarget
    const newTrack = oldTrack.cloneNode(true)

    newTrack.src = captionsUrl

    oldTrack.parentNode.replaceChild(newTrack, oldTrack)
  }

  // =============================================================================
  // VIDEO LOADING
  // =============================================================================

  sourceValueChanged(value, oldValue) {
    if (this.skipSourceValueChange === value) {
      this.skipSourceValueChange = null
      return
    }

    // Only load new video if we have a player, values are different
    if (this.player && value && value !== oldValue) {
      this.isPlaying = false
      this.pendingPlay = false
      this.loadNewVideo(value).catch(() => {
        // Failed to load new video, but don't break the UI
      })
    }
  }

  // Load a new video
  loadNewVideo(source, seekToSeconds = null) {
    if (!this.player) {
      return Promise.reject(new Error("Player not initialized"))
    }
    this.skipSourceValueChange = source
    this.sourceValue = source

    // Reset playing states when loading new video
    this.isPlaying = false
    this.pendingPlay = false
    delete this.player.dataset.sourceOrientation

    return new Promise((resolve, reject) => {
      try {
        // Set up event listener for when video is ready
        const onCanPlay = () => {
          this.player.removeEventListener("can-play", onCanPlay)

          // Seek to specific time if provided
          if (seekToSeconds !== null) {
            this.syncCurrentTimeAttribute(seekToSeconds)
          }

          resolve()
        }

        // Set up error handler
        const onError = (error) => {
          this.player.removeEventListener("can-play", onCanPlay)
          this.player.removeEventListener("error", onError)
          reject(error)
        }

        this.player.addEventListener("can-play", onCanPlay)
        this.player.addEventListener("error", onError)

        // Update the player source - this will trigger loading
        this.player.src = source
        this.player.setAttribute("src", source)
      } catch (error) {
        reject(error)
      }
    })
  }
}
